import React from "react";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";

interface PaginationProps {
  total: number;
  perPage: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  onPerPageChange?: (perPage: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
  total, perPage, currentPage, onPageChange, onPerPageChange,
}) => {
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const safePage   = Math.min(currentPage, totalPages);
  const startIndex = total === 0 ? 0 : (safePage - 1) * perPage;
  const endIndex   = Math.min(safePage * perPage, total);

  const btnBase = `flex items-center justify-center min-w-[36px] h-9 px-3
    rounded-xl text-sm font-medium border cursor-pointer
    transition-all duration-200 disabled:opacity-40
    disabled:cursor-not-allowed`;

  return (
    <div className="flex flex-wrap items-center justify-between gap-4
                    px-5 py-4 bg-white rounded-2xl
                    border border-blue-100 shadow-sm mt-6">

      {/* Info */}
      <p className="text-xs text-slate-400 font-medium m-0">
        Affichage de{" "}
        <span className="text-blue-600 font-semibold">{startIndex + 1}</span>
        {" "}à{" "}
        <span className="text-blue-600 font-semibold">{endIndex}</span>
        {" "}sur{" "}
        <span className="text-blue-600 font-semibold">{total}</span>
        {" "}résultats
      </p>

      {/* Controls */}
      <div className="flex items-center gap-2 flex-wrap">

        {/* Per page select */}
        {onPerPageChange && (
          <select
            value={perPage}
            onChange={(e) => onPerPageChange(Number(e.target.value))}
            className="h-9 px-3 rounded-xl text-xs font-medium text-blue-900
                       border border-blue-200 bg-white cursor-pointer
                       focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100
                       hover:border-blue-300 transition-all duration-200"
          >
            <option value={6}>6 par page</option>
            <option value={12}>12 par page</option>
            <option value={18}>18 par page</option>
          </select>
        )}

        {/* Prev */}
        <button
          onClick={() => onPageChange(Math.max(1, safePage - 1))}
          disabled={safePage === 1}
          className={`${btnBase} border-slate-200 bg-white text-slate-600
                      hover:bg-blue-500 hover:text-white hover:border-blue-500
                      disabled:hover:bg-white disabled:hover:text-slate-400
                      disabled:hover:border-slate-200`}
        >
          <LeftOutlined style={{ fontSize: 11 }} />
        </button>

        {/* Page numbers */}
        {Array.from({ length: totalPages }, (_, i) => {
          const page = i + 1;
          const isActive = safePage === page;

          if (
            page === 1 ||
            page === totalPages ||
            (page >= safePage - 1 && page <= safePage + 1)
          ) {
            return (
              <button
                key={page}
                onClick={() => onPageChange(page)}
                className={`${btnBase}
                  ${isActive
                    ? "bg-[#0F52BA] text-white border-blue-500 shadow-md shadow-blue-200 scale-105"
                    : "border-slate-200 bg-white text-slate-600 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-600"
                  }`}
              >
                {page}
              </button>
            );
          } else if (page === safePage - 2 || page === safePage + 2) {
            return (
              <span key={page} className="text-slate-400 text-sm px-1 select-none">
                …
              </span>
            );
          }
          return null;
        })}

        {/* Next */}
        <button
          onClick={() => onPageChange(Math.min(totalPages, safePage + 1))}
          disabled={safePage === totalPages}
          className={`${btnBase} border-slate-200 bg-white text-slate-600
                      hover:bg-blue-500 hover:text-white hover:border-blue-500
                      disabled:hover:bg-white disabled:hover:text-slate-400
                      disabled:hover:border-slate-200`}
        >
          <RightOutlined style={{ fontSize: 11 }} />
        </button>

      </div>
    </div>
  );
};

export default Pagination;